import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ShieldAlert, Calendar, Inbox, Loader2, KeyRound, Home } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · 6 Sep 2026 · User-Directive „Downgrade-Preview-Modal".
 * Zeigt VOR dem PIN-Prompt eine transparente Aufstellung der offenen
 * Buchungen und Anfragen, die durch den Downgrade automatisch storniert
 * werden. Analog zum bestehenden DeletionPreviewModal, aber schlanker
 * (Downgrade ist reversibel — nur Storno statt Purge).
 */
export default function DowngradePreviewModal({ open, row, onCancel, onContinue }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const rowId = row?.id;
  const onCancelRef = useRef(onCancel);
  useEffect(() => { onCancelRef.current = onCancel; });

  useEffect(() => {
    if (!open || !rowId) { setPreview(null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/allowed-emails/${rowId}/downgrade-preview`);
        setPreview(data);
      } catch (e) {
        toast.error(formatErr(e.response?.data?.detail) || e.message);
        onCancelRef.current?.();
      } finally { setLoading(false); }
    })();
  }, [open, rowId]);

  if (!open || !row) return null;
  const bookings = preview?.bookings_open ?? 0;
  const requests = preview?.requests_open ?? 0;
  // Feb 2026 · 9 Sep 2026 · Objektverwaltung — Downgrade archiviert auch alle
  // Kunden-Objekte in den „Entfernte Objekte"-Bereich. Der Admin sieht die
  // Zahl vor dem PIN-Prompt, damit die Aktion transparent bleibt.
  const properties = preview?.properties_open ?? 0;
  const nothingToCancel = !loading && bookings === 0 && requests === 0 && properties === 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(13,25,48,0.6)" }}
      onClick={onCancel}
      data-testid="downgrade-preview-modal"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schliessen"
          data-testid="downgrade-preview-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <div className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(STATUS_COLORS.grey, 0.6), color: BRAND_COLORS.navyDark }}
          >
            <ShieldAlert className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight mb-1">
            Kunden auf Gast herabstufen?
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            <b>{row.email}</b> wird auf Rolle „Gast" umgestellt. Alle
            bezahlpflichtigen Aktionen werden sofort gesperrt und zeigen
            das Schloss-Icon.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 text-navy-dark/60 text-[13px] py-6 justify-center" data-testid="downgrade-preview-loading">
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.6} />
              Offene Vorgaenge werden gezaehlt ...
            </div>
          ) : nothingToCancel ? (
            <div
              className="border p-4 mb-5"
              data-testid="downgrade-preview-nothing"
              style={{ borderColor: withAlpha(STATUS_COLORS.green, 0.5), background: withAlpha(STATUS_COLORS.green, 0.08) }}
            >
              <div className="text-[13px] text-navy leading-snug">
                Keine offenen Buchungen oder Anfragen — der Downgrade
                laeuft ohne Nebenwirkungen.
              </div>
            </div>
          ) : (
            <div
              className="border p-4 mb-5"
              data-testid="downgrade-preview-cascade"
              style={{ borderColor: withAlpha(STATUS_COLORS.grey, 1), background: withAlpha(STATUS_COLORS.grey, 0.35) }}
            >
              <div className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70 mb-2">
                Automatisch storniert
              </div>
              <ul className="space-y-2">
                {bookings > 0 && (
                  <li className="flex items-center gap-3 text-[13.5px] text-navy" data-testid="downgrade-preview-bookings">
                    <Calendar className="w-4 h-4 text-navy-dark/60" strokeWidth={1.6} />
                    <span><b className="tabular-nums">{bookings}</b> offene {bookings === 1 ? "Buchung wird storniert" : "Buchungen werden storniert"}</span>
                  </li>
                )}
                {requests > 0 && (
                  <li className="flex items-center gap-3 text-[13.5px] text-navy" data-testid="downgrade-preview-requests">
                    <Inbox className="w-4 h-4 text-navy-dark/60" strokeWidth={1.6} />
                    <span><b className="tabular-nums">{requests}</b> offene {requests === 1 ? "Anfrage wird storniert" : "Anfragen werden storniert"}</span>
                  </li>
                )}
                {properties > 0 && (
                  <li className="flex items-center gap-3 text-[13.5px] text-navy" data-testid="downgrade-preview-properties">
                    <Home className="w-4 h-4 text-navy-dark/60" strokeWidth={1.6} />
                    <span><b className="tabular-nums">{properties}</b> {properties === 1 ? 'Objekt wandert in „Entfernte Objekte"' : 'Objekte wandern in „Entfernte Objekte"'}</span>
                  </li>
                )}
              </ul>
              <div className="text-[11.5px] text-navy-dark/60 italic mt-3 leading-snug">
                Alle Daten bleiben erhalten. Bei spaeterer Reaktivierung koennen
                Buchungen, Anfragen und Objekte automatisch wiederhergestellt
                werden.
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-3 py-2"
              data-testid="downgrade-preview-cancel"
            >
              Abbrechen
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => onContinue?.({ bookings, requests })}
              data-testid="downgrade-preview-continue"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
              style={{ background: BRAND_COLORS.navy }}
            >
              <KeyRound className="w-3.5 h-3.5" strokeWidth={1.8} />
              Weiter zur PIN-Eingabe
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
